import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { EmployeeResponse } from '../models/employee-response.model';
import {
  Employee,
  Department,
  PayGroup,
  WorkerType,
  WorkOrder,
  Person,
  Company,
  Shift,
  MissingDataEmployee,
  CountryPhoneCode,
  ParsedPhoneNumber,
  Role,
  EmployeeListFilters,
  EmploymentStatus,
  UserWorkOrderAssignment,
} from '../models/employee.model';

/** Generic paged response shape used by several endpoints */
export interface ApiPagedResponse<T> {
  status: 'success' | 'error';
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  data: T[];
}

export interface ApiStatusResponse {
  status: string;
  message?: string | null;
  data?: any;
}

export interface PatchUserBody {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  employeeNumber?: string | null;
  departmentId?: number | null;
  phoneNumber?: string | null;
  reportToId?: number | null;
  status?: EmploymentStatus | 'OnLeave';
  payGroupId?: number | null;
  workerTypeId?: number | null;
  defaultWorkOrderNumberId?: number | null;
  companyId?: number | null;
  shiftId?: number | null;
}

export interface RoleAssignmentBody {
  userId: string;
  roleId: string;
}

export interface UserWonMutationBody {
  employeeId: number;
  workOrderNumberId: number;
}

export interface RegisterEmployeeBody {
  employeeNumber: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  hireDate: string;
  phoneNumber: string;
  email: string;
  rfid?: string;
  department: number;
  defaultWorkOrderNumber?: string;
  companyName: string;
  payGroup: string;
  shift: string;
  workerType: string;
  supervisorNumber?: string;
}


@Injectable({ providedIn: 'root' })
export class EmployeesService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly adminRoleId = 1;

  private readonly countryPhoneCodes: CountryPhoneCode[] = [
    { iso2: 'US', name: 'United States', dialCode: '1' },
    { iso2: 'CA', name: 'Canada', dialCode: '1' },
    { iso2: 'MX', name: 'Mexico', dialCode: '52' },
    { iso2: 'DO', name: 'Dominican Republic', dialCode: '1' },
    { iso2: 'PR', name: 'Puerto Rico', dialCode: '1' },
    { iso2: 'GB', name: 'United Kingdom', dialCode: '44' },
    { iso2: 'IE', name: 'Ireland', dialCode: '353' },
    { iso2: 'FR', name: 'France', dialCode: '33' },
    { iso2: 'DE', name: 'Germany', dialCode: '49' },
    { iso2: 'ES', name: 'Spain', dialCode: '34' },
    { iso2: 'IT', name: 'Italy', dialCode: '39' },
    { iso2: 'PT', name: 'Portugal', dialCode: '351' },
    { iso2: 'NL', name: 'Netherlands', dialCode: '31' },
    { iso2: 'BE', name: 'Belgium', dialCode: '32' },
    { iso2: 'CH', name: 'Switzerland', dialCode: '41' },
    { iso2: 'SE', name: 'Sweden', dialCode: '46' },
    { iso2: 'NO', name: 'Norway', dialCode: '47' },
    { iso2: 'DK', name: 'Denmark', dialCode: '45' },
    { iso2: 'PL', name: 'Poland', dialCode: '48' },
    { iso2: 'CZ', name: 'Czech Republic', dialCode: '420' },
    { iso2: 'SK', name: 'Slovakia', dialCode: '421' },
    { iso2: 'HU', name: 'Hungary', dialCode: '36' },
    { iso2: 'RO', name: 'Romania', dialCode: '40' },
    { iso2: 'GR', name: 'Greece', dialCode: '30' },
    { iso2: 'TR', name: 'Turkey', dialCode: '90' },
    { iso2: 'IL', name: 'Israel', dialCode: '972' },
    { iso2: 'AE', name: 'United Arab Emirates', dialCode: '971' },
    { iso2: 'SA', name: 'Saudi Arabia', dialCode: '966' },
    { iso2: 'EG', name: 'Egypt', dialCode: '20' },
    { iso2: 'ZA', name: 'South Africa', dialCode: '27' },
    { iso2: 'IN', name: 'India', dialCode: '91' },
    { iso2: 'PK', name: 'Pakistan', dialCode: '92' },
    { iso2: 'BD', name: 'Bangladesh', dialCode: '880' },
    { iso2: 'CN', name: 'China', dialCode: '86' },
    { iso2: 'JP', name: 'Japan', dialCode: '81' },
    { iso2: 'KR', name: 'South Korea', dialCode: '82' },
    { iso2: 'PH', name: 'Philippines', dialCode: '63' },
    { iso2: 'TH', name: 'Thailand', dialCode: '66' },
    { iso2: 'VN', name: 'Vietnam', dialCode: '84' },
    { iso2: 'MY', name: 'Malaysia', dialCode: '60' },
    { iso2: 'SG', name: 'Singapore', dialCode: '65' },
    { iso2: 'AU', name: 'Australia', dialCode: '61' },
    { iso2: 'NZ', name: 'New Zealand', dialCode: '64' },
    { iso2: 'BR', name: 'Brazil', dialCode: '55' },
    { iso2: 'AR', name: 'Argentina', dialCode: '54' },
    { iso2: 'CL', name: 'Chile', dialCode: '56' },
    { iso2: 'CO', name: 'Colombia', dialCode: '57' },
    { iso2: 'PE', name: 'Peru', dialCode: '51' },
  ];

  constructor(private http: HttpClient) { }

  private getAllPagedResults<T>(
    url: string,
    options?: {
      pageParamName?: 'page' | 'pageNumber';
      pageSize?: number;
      extraParams?: Record<string, string | number | boolean | null | undefined>;
    },
  ): Observable<T[]> {
    const pageParamName = options?.pageParamName ?? 'page';
    const pageSize = options?.pageSize ?? 200;
    const extraParams = options?.extraParams ?? {};

    const buildParams = (page: number): HttpParams => {
      let params = new HttpParams().set(pageParamName, page).set('pageSize', pageSize);

      for (const [key, value] of Object.entries(extraParams)) {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      }

      return params;
    };

    const fetchPage = (page: number): Observable<ApiPagedResponse<T>> =>
      this.http.get<ApiPagedResponse<T>>(url, {
        params: buildParams(page),
      });

    return fetchPage(1).pipe(
      expand((res) => (res.currentPage < res.totalPages ? fetchPage(res.currentPage + 1) : of())),
      map((res: ApiPagedResponse<T> | undefined) => res?.data ?? []),
      reduce((acc: T[], pageItems: T[]) => acc.concat(pageItems), []),
    );
  }

  private buildEmployeeListParams(
    page: number,
    pageSize: number,
    filters?: EmployeeListFilters,
  ): HttpParams {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);

    const companyId = filters?.companyId ?? null;
    const departmentId = filters?.departmentId ?? null;
    const employmentStatus = filters?.employmentStatus ?? null;
    const searchTerm = String(filters?.searchTerm ?? '').trim();

    if (typeof companyId === 'number') {
      params = params.set('companyId', String(companyId));
    }

    if (typeof departmentId === 'number') {
      params = params.set('departmentId', String(departmentId));
    }

    if (employmentStatus) {
      params = params.set('employmentStatus', employmentStatus);
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return params;
  }

  private mapUserToPerson(row: any): Person {
    return {
      id: Number(row?.id),
      firstName: String(row?.firstName ?? ''),
      middleName: row?.middleName ? String(row.middleName) : null,
      lastName: String(row?.lastName ?? ''),
      email: row?.email ?? null,
      employeeNumber: row?.employeeNumber ?? null,
      employmentStatus: row?.employmentStatus ?? row?.employeeStatus ?? null,
    };
  }

  /** Fetch paginated employees (list endpoint) */
  getEmployees(
    page: number = 1,
    pageSize: number = 100,
    filters?: EmployeeListFilters,
  ): Observable<EmployeeResponse> {
    const params = this.buildEmployeeListParams(page, pageSize, filters);
    return this.http.get<EmployeeResponse>(`${this.baseUrl}/api/users`, { params });
  }

  /** Fetch all employees across all pages with optional filters */
  getAllEmployees(
    pageSize: number = 100,
    filters?: EmployeeListFilters,
  ): Observable<Person[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/users`, {
      pageParamName: 'page',
      pageSize,
      extraParams: {
        companyId: filters?.companyId ?? null,
        departmentId: filters?.departmentId ?? null,
        employmentStatus: filters?.employmentStatus ?? null,
        searchTerm: String(filters?.searchTerm ?? '').trim() || null,
      },
    }).pipe(map((rows) => rows.map((row) => this.mapUserToPerson(row))));
  }

  /** Full supervisor dropdown source */
  getAllEmployeesForSupervisorSelect(pageSize: number = 100): Observable<Person[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/users`, {
      pageParamName: 'page',
      pageSize,
    }).pipe(map((rows) => rows.map((row) => this.mapUserToPerson(row))));
  }

  /** Missing employee data list */
  getEmployeesMissingData(filters?: EmployeeListFilters): Observable<MissingDataEmployee[]> {
    return this.http
      .get<{ status: string; data: any[] }>(`${this.baseUrl}/api/users/missing-default`)
      .pipe(
        map((res) =>
          (res.data ?? [])
            .map((e): MissingDataEmployee => ({
              id: e.id,

              employeeStatus: e.employeeStatus ?? null,
              employmentStatus: e.employmentStatus ?? e.employeeStatus ?? null,

              firstName: e.firstName ?? '',
              lastName: e.lastName ?? '',
              employeeNumber: e.employeeNumber ?? null,

              defaultWorkOrderNumber: e.defaultWorkOrderNumber
                ? {
                  id: e.defaultWorkOrderNumber.id,
                  won: e.defaultWorkOrderNumber.won,
                  description: e.defaultWorkOrderNumber.description ?? null,
                }
                : null,

              departmentWorkerType: e.departmentWorkerType
                ? {
                  id: e.departmentWorkerType.id,
                  name: e.departmentWorkerType.name,
                }
                : null,

              department: e.department
                ? {
                  id: e.department.id,
                  code: String(e.department.code),
                  name: e.department.name,
                }
                : null,

              shift: e.shift
                ? {
                  id: e.shift.id,
                  name: e.shift.name,
                  gracePeriodMinutes:
                    typeof e.shift.gracePeriodMinutes === 'number'
                      ? e.shift.gracePeriodMinutes
                      : null,
                  departments:
                    typeof e.shift.departments === 'number'
                      ? e.shift.departments
                      : null,
                  payCodes: Array.isArray(e.shift.payCodes)
                    ? e.shift.payCodes.map((pc: any) => ({
                      id: pc.id,
                      name: pc.name,
                      description: pc.description ?? null,
                      type: pc.type ?? null,
                    }))
                    : [],
                }
                : null,

              payGroup: e.payGroup
                ? {
                  id: e.payGroup.id,
                  name: e.payGroup.name,
                }
                : null,

              scheduleGroup: e.scheduleGroup
                ? {
                  id: e.scheduleGroup.id,
                  name: e.scheduleGroup.name,
                  schedules: Array.isArray(e.scheduleGroup.schedules)
                    ? e.scheduleGroup.schedules
                    : [],
                }
                : null,

              company: e.company
                ? {
                  id: e.company.id,
                  name: e.company.name,
                  description: e.company.description ?? null,
                  isSubtractor: !!e.company.isSubtractor,
                }
                : null,
            }))
            .filter((employee) => this.matchesMissingDataFilters(employee, filters)),
        ),
      );
  }

  private matchesMissingDataFilters(
    employee: MissingDataEmployee,
    filters?: EmployeeListFilters,
  ): boolean {
    if (!filters) {
      return true;
    }

    if (
      typeof filters.companyId === 'number' &&
      employee.company?.id !== filters.companyId
    ) {
      return false;
    }

    if (
      typeof filters.departmentId === 'number' &&
      employee.department?.id !== filters.departmentId
    ) {
      return false;
    }

    if (
      filters.employmentStatus &&
      (employee.employmentStatus ?? employee.employeeStatus) !== filters.employmentStatus
    ) {
      return false;
    }

    const searchTerm = String(filters.searchTerm ?? '').trim().toLowerCase();

    if (searchTerm) {
      const searchable = [
        employee.firstName,
        employee.lastName,
        employee.employeeNumber,
        employee.company?.name,
        employee.department?.code,
        employee.department?.name,
        employee.departmentWorkerType?.name,
        employee.payGroup?.name,
        employee.shift?.name,
        employee.scheduleGroup?.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(searchTerm);
    }

    return true;
  }

  /** Get employee by ID (detail endpoint) */
  getEmployeeById(id: number): Observable<Employee | null> {
    return this.http
      .get<{ status: string; data: Employee }>(`${this.baseUrl}/api/users/${id}`)
      .pipe(
        map((res) => this.mapEmployee(res.data)),
        catchError((err) => {
          console.error('Error fetching employee by ID:', err);
          return of(null);
        }),
      );
  }

  /** Create employee */
  createEmployee(body: RegisterEmployeeBody): Observable<ApiStatusResponse> {
    return this.http.post<ApiStatusResponse>(`${this.baseUrl}/api/account/register`, body);
  }

  /** PATCH user fields */
  patchUser(id: number, body: PatchUserBody): Observable<Employee> {
    const normalizedBody: PatchUserBody = {
      ...body,
      phoneNumber:
        body.phoneNumber === undefined
          ? undefined
          : this.normalizePhoneForStorage(body.phoneNumber),
    };

    return this.http
      .patch<{ status: string; data: Employee }>(`${this.baseUrl}/api/users/${id}`, normalizedBody)
      .pipe(map((res) => this.mapEmployee(res.data)));
  }

  deleteUser(id: number): Observable<ApiStatusResponse> {
    return this.http.delete<ApiStatusResponse>(`${this.baseUrl}/api/users/${id}`);
  }

  /** PATCH assign RFID */
  assignRfid(id: number, rfid: string | null): Observable<Employee> {
    return this.http
      .patch<{ status: string; data: Employee }>(`${this.baseUrl}/api/users/assign-RFID/${id}`, {
        RFID: rfid ?? '',
      })
      .pipe(map((res) => res.data));
  }

  /** ---- role helpers ---- */

  hasAdminRole(employee: Employee | null | undefined): boolean {
    return !!employee?.roles?.some((role) => Number(role.id) === this.adminRoleId);
  }

  assignAdminRole(userId: number | string): Observable<ApiStatusResponse> {
    return this.assignRole(userId, this.adminRoleId);
  }

  unassignAdminRole(userId: number | string): Observable<ApiStatusResponse> {
    return this.unassignRole(userId, this.adminRoleId);
  }

  assignRole(userId: number | string, roleId: number | string): Observable<ApiStatusResponse> {
    const body: RoleAssignmentBody = {
      userId: String(userId),
      roleId: String(roleId),
    };

    return this.http.post<ApiStatusResponse>(`${this.baseUrl}/api/roles/assign-role`, body);
  }

  unassignRole(userId: number | string, roleId: number | string): Observable<ApiStatusResponse> {
    const body: RoleAssignmentBody = {
      userId: String(userId),
      roleId: String(roleId),
    };

    return this.http.request<ApiStatusResponse>('delete', `${this.baseUrl}/api/roles/unassign-role`, {
      body,
    });
  }

  /** ---- phone helpers ---- */

  getCountryPhoneCodes(): CountryPhoneCode[] {
    return [...this.countryPhoneCodes].sort((a, b) => a.name.localeCompare(b.name));
  }

  searchCountryPhoneCodes(searchTerm: string): CountryPhoneCode[] {
    const term = (searchTerm ?? '').trim().toLowerCase();
    if (!term) {
      return this.getCountryPhoneCodes();
    }

    return this.getCountryPhoneCodes().filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.iso2.toLowerCase().includes(term) ||
        c.dialCode.includes(term.replace(/\D/g, '')),
    );
  }

  normalizePhoneForStorage(value: string | null | undefined): string | null {
    if (value == null) return null;

    const digits = String(value).replace(/\D/g, '').trim();
    return digits.length ? digits : null;
  }

  parsePhoneNumber(value: string | null | undefined): ParsedPhoneNumber {
    const normalized = this.normalizePhoneForStorage(value);

    if (!normalized) {
      return {
        fullNumber: null,
        countryCode: '1',
        nationalNumber: '',
        country: this.getDefaultCountryPhoneCode(),
      };
    }

    if (normalized.length === 10) {
      const defaultCountry = this.getDefaultCountryPhoneCode();
      return {
        fullNumber: `${defaultCountry.dialCode}${normalized}`,
        countryCode: defaultCountry.dialCode,
        nationalNumber: normalized,
        country: defaultCountry,
      };
    }

    const sortedCodes = [...this.countryPhoneCodes].sort(
      (a, b) => b.dialCode.length - a.dialCode.length,
    );

    const matchedCountry =
      sortedCodes.find((c) => normalized.startsWith(c.dialCode)) ?? this.getDefaultCountryPhoneCode();

    let nationalNumber = normalized;

    if (normalized.startsWith(matchedCountry.dialCode)) {
      nationalNumber = normalized.slice(matchedCountry.dialCode.length);
    }

    return {
      fullNumber: normalized,
      countryCode: matchedCountry.dialCode,
      nationalNumber,
      country: matchedCountry,
    };
  }

  buildPhoneNumberForApi(
    countryCode: string | null | undefined,
    nationalNumber: string | null | undefined,
  ): string | null {
    const cc = String(countryCode ?? '').replace(/\D/g, '');
    const nn = String(nationalNumber ?? '').replace(/\D/g, '');

    if (!cc && !nn) return null;
    if (!nn) return null;

    return `${cc}${nn}`;
  }

  formatPhoneForDisplay(value: string | null | undefined): string {
    const parsed = this.parsePhoneNumber(value);

    if (!parsed.fullNumber || !parsed.nationalNumber) {
      return '';
    }

    if (parsed.countryCode === '1') {
      const digits = parsed.nationalNumber.replace(/\D/g, '');

      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }

      if (digits.length === 7) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
    }

    return `+${parsed.countryCode} ${this.groupPhoneDigits(parsed.nationalNumber)}`.trim();
  }

  private groupPhoneDigits(value: string): string {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';

    const parts: string[] = [];
    let remaining = digits;

    while (remaining.length > 4) {
      parts.push(remaining.slice(0, remaining.length > 8 ? 3 : 4));
      remaining = remaining.slice(remaining.length > 8 ? 3 : 4);
    }

    if (remaining.length) {
      parts.push(remaining);
    }

    return parts.join(' ');
  }

  private getDefaultCountryPhoneCode(): CountryPhoneCode {
    return this.countryPhoneCodes.find((c) => c.iso2 === 'US') ?? this.countryPhoneCodes[0];
  }

  /** ---- dropdown data ---- */

  getAllCompanies(pageSize: number = 200): Observable<Company[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/companies`, {
      pageParamName: 'pageNumber',
      pageSize,
      extraParams: { orderBy: 'name' },
    }).pipe(
      map((rows) =>
        rows.map(
          (c): Company => ({
            id: c.id,
            name: c.name,
            description: c.description ?? null,
            isSubtractor: !!c.isSubtractor,
          }),
        ),
      ),
    );
  }

  getAllDepartments(pageSize: number = 200): Observable<Department[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/departments`, {
      pageParamName: 'pageNumber',
      pageSize,
      extraParams: { orderBy: 'codeAsc' },
    }).pipe(
      map((rows) =>
        rows.map(
          (d): Department => ({
            id: d.id,
            name: d.name,
            code: String(d.code),
          }),
        ),
      ),
    );
  }

  getPayGroups(pageSize: number = 200): Observable<PayGroup[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/pay-groups`, {
      pageParamName: 'page',
      pageSize,
    }).pipe(
      map((rows) =>
        rows.map(
          (p): PayGroup => ({
            id: p.id,
            name: p.name,
          }),
        ),
      ),
    );
  }

  getWorkerTypes(pageSize: number = 200): Observable<WorkerType[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/worker-types`, {
      pageParamName: 'page',
      pageSize,
    }).pipe(
      map((rows) =>
        rows.map(
          (w): WorkerType => ({
            id: w.id,
            name: w.name,
          }),
        ),
      ),
    );
  }

  getShifts(pageSize: number = 200): Observable<Shift[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/shifts`, {
      pageParamName: 'page',
      pageSize,
    }).pipe(
      map((rows) =>
        rows.map(
          (s): Shift => ({
            id: s.id,
            name: s.name,
            gracePeriodMinutes:
              typeof s.gracePeriodMinutes === 'number' ? s.gracePeriodMinutes : null,
            departments: typeof s.departments === 'number' ? s.departments : null,
            payCodes: Array.isArray(s.payCodes)
              ? s.payCodes.map((pc: any) => ({
                id: pc.id,
                name: pc.name,
                description: pc.description ?? null,
                type: pc.type ?? null,
              }))
              : [],
          }),
        ),
      ),
    );
  }

  getOverheadWorkOrders(pageSize: number = 200): Observable<WorkOrder[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/work-order-numbers`, {
      pageParamName: 'page',
      pageSize,
      extraParams: { isOverhead: true },
    }).pipe(
      map((rows) =>
        rows.map(
          (w): WorkOrder => ({
            id: w.id,
            won: w.won,
            description: w.description ?? null,
          }),
        ),
      ),
    );
  }


  /** Fetch every work order across all pages for employee assignment. */
  getAllWorkOrders(pageSize: number = 200): Observable<WorkOrder[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/work-order-numbers`, {
      pageParamName: 'pageNumber',
      pageSize,
    }).pipe(
      map((rows) =>
        rows.map(
          (w): WorkOrder => ({
            id: Number(w.id),
            won: String(w.won ?? ''),
            description: w.description ?? null,
            isActive: typeof w.isActive === 'boolean' ? w.isActive : true,
            isOverhead: !!w.isOverhead,
            project: w.project
              ? {
                  id: Number(w.project.id),
                  name: String(w.project.name ?? ''),
                }
              : null,
            department: w.department
              ? {
                  id: Number(w.department.id),
                  code: String(w.department.code ?? ''),
                  name: String(w.department.name ?? ''),
                }
              : null,
          }),
        ),
      ),
    );
  }

  /** Fetch every WON currently assigned to one employee across all pages. */
  getEmployeeAssignedWorkOrders(
    employeeId: number,
    pageSize: number = 200,
  ): Observable<UserWorkOrderAssignment[]> {
    return this.getAllPagedResults<any>(`${this.baseUrl}/api/user-wons`, {
      pageParamName: 'pageNumber',
      pageSize,
      extraParams: { employeeId },
    }).pipe(
      map((rows) =>
        rows.map(
          (row): UserWorkOrderAssignment => ({
            id: Number(row.id),
            employee: {
              id: Number(row.employee?.id),
              firstName: String(row.employee?.firstName ?? ''),
              lastName: String(row.employee?.lastName ?? ''),
              employeeNumber: row.employee?.employeeNumber ?? null,
            },
            workOrderNumber: {
              id: Number(row.workOrderNumber?.id),
              won: String(row.workOrderNumber?.won ?? ''),
              description: row.workOrderNumber?.description ?? null,
            },
          }),
        ),
      ),
    );
  }

  /** Assign one WON to one employee. Each selected WON calls this endpoint separately. */
  assignWorkOrderToEmployee(
    employeeId: number,
    workOrderNumberId: number,
  ): Observable<ApiStatusResponse> {
    const body: UserWonMutationBody = {
      employeeId,
      workOrderNumberId,
    };

    return this.http.post<ApiStatusResponse>(`${this.baseUrl}/api/user-wons`, body);
  }

  /** Remove one WON assignment from one employee. */
  removeWorkOrderFromEmployee(
    employeeId: number,
    workOrderNumberId: number,
  ): Observable<ApiStatusResponse> {
    const body: UserWonMutationBody = {
      employeeId,
      workOrderNumberId,
    };

    return this.http.request<ApiStatusResponse>('delete', `${this.baseUrl}/api/user-wons`, {
      body,
    });
  }

  toggleAutoBooking(id: number): Observable<Employee> {
    return this.http
      .patch<{ status: string; data: Employee }>(
        `${this.baseUrl}/api/users/${id}/toggle-auto-booking`,
        {},
      )
      .pipe(map((res) => this.mapEmployee(res.data)));
  }

  private mapEmployee(row: Employee | null | undefined): Employee {
    return {
      ...(row as Employee),
      phoneNumber: this.normalizePhoneForStorage(row?.phoneNumber ?? null),
      sickLeavePolicy: row?.sickLeavePolicy ?? null,
      vacationLeavePolicy: row?.vacationLeavePolicy ?? null,
      employeeLeaveBalances: Array.isArray(row?.employeeLeaveBalances)
        ? row.employeeLeaveBalances
        : [],
      roles: Array.isArray(row?.roles)
        ? row.roles.map(
          (r: any): Role => ({
            id: Number(r.id),
            name: String(r.name ?? ''),
          }),
        )
        : [],
    };
  }
}